// components/Technologist/TechDashboard.jsx
// Exam Room / Technologist Dashboard — ported from RIS examroom.php
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Grid, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Tooltip, Autocomplete, CircularProgress,
  Alert, Snackbar, AppBar, Toolbar, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import {
  PlayArrow as StartIcon, Stop as StopIcon, CheckCircle as QCIcon,
  AssignmentInd as AssignIcon, Refresh as RefreshIcon,
  MedicalServices as MedIcon, Logout as LogoutIcon
} from '@mui/icons-material';
import { orderService } from '../../services/ris';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const STATUS_COLORS = {
  READY: 'warning', STARTED: 'primary', ENDEXAM: 'success', QC: 'info'
};

export default function TechDashboard() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [radiologists, setRadiologists] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Filters
  const [filters, setFilters] = useState({
    mrn: '', patientName: '', modality: '', dateFrom: '', dateTo: ''
  });

  // Load radiologists for assignment dropdown
  useEffect(() => {
    const loadRads = async () => {
      try {
        const { data } = await api.get('/users', { params: { role: 'radiologist', limit: 100 } });
        setRadiologists(data.users || []);
      } catch (err) {
        console.error('Failed to load radiologists:', err);
      }
    };
    loadRads();
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: 'EXAM ROOM',
        limit: 200
      };
      if (filters.mrn) params.mrn = filters.mrn;
      if (filters.patientName) params.patientName = filters.patientName;
      if (filters.modality) params.modality = filters.modality;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;

      const { data } = await orderService.list(params);
      setOrders(data.orders || []);
    } catch (err) {
      console.error('Failed to fetch exam room orders:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Auto-refresh every 3 minutes
  useEffect(() => {
    const interval = setInterval(fetchOrders, 180000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const handleStatusAction = async (orderId, newStatus, label) => {
    try {
      await orderService.updateStatus(orderId, newStatus);
      setSnackbar({ open: true, message: `${label} successful`, severity: 'success' });
      fetchOrders();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || `Failed to ${label}`, severity: 'error' });
    }
  };

  const handleAssignRadiologist = async (orderId, radId, radName) => {
    try {
      await orderService.assignRadiologist(orderId, radId, radName);
      setSnackbar({ open: true, message: 'Radiologist assigned & sent to worklist', severity: 'success' });
      fetchOrders();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to assign', severity: 'error' });
    }
  };

  const renderActions = (order) => {
    const s = order.orderStatus;
    return (
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        {s === 'READY' && (
          <Tooltip title="Start Exam">
            <Button size="small" variant="contained" color="primary"
              startIcon={<StartIcon />}
              onClick={() => handleStatusAction(order._id, 'STARTED', 'Exam started')}>
              Start
            </Button>
          </Tooltip>
        )}
        {s === 'STARTED' && (
          <Tooltip title="Complete Exam">
            <Button size="small" variant="contained" color="success"
              startIcon={<StopIcon />}
              onClick={() => handleStatusAction(order._id, 'ENDEXAM', 'Exam completed')}>
              Complete
            </Button>
          </Tooltip>
        )}
        {s === 'ENDEXAM' && (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Tooltip title="QC Review">
              <Button size="small" variant="outlined" color="info"
                startIcon={<QCIcon />}
                onClick={() => handleStatusAction(order._id, 'QC', 'Sent to QC')}>
                QC
              </Button>
            </Tooltip>
            <Autocomplete
              size="small"
              options={radiologists}
              getOptionLabel={(r) => `${r.firstName} ${r.lastName}`}
              onChange={(_, rad) => {
                if (rad) handleAssignRadiologist(order._id, rad._id, `${rad.firstName} ${rad.lastName}`);
              }}
              sx={{ minWidth: 180 }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Assign Radiologist" size="small" />
              )}
            />
          </Box>
        )}
        {s === 'QC' && (
          <Autocomplete
            size="small"
            options={radiologists}
            getOptionLabel={(r) => `${r.firstName} ${r.lastName}`}
            onChange={(_, rad) => {
              if (rad) handleAssignRadiologist(order._id, rad._id, `${rad.firstName} ${rad.lastName}`);
            }}
            sx={{ minWidth: 200 }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Assign Radiologist" size="small" />
            )}
          />
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Top Bar */}
      <AppBar position="static" color="primary" elevation={1}>
        <Toolbar variant="dense">
          <MedIcon sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Exam Room — Technologist Worklist
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.firstName} {user?.lastName}
          </Typography>
          <IconButton color="inherit" onClick={logout}><LogoutIcon /></IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" label="MRN" value={filters.mrn}
                onChange={e => setFilters(f => ({ ...f, mrn: e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" label="Patient Name" value={filters.patientName}
                onChange={e => setFilters(f => ({ ...f, patientName: e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Modality</InputLabel>
                <Select value={filters.modality} label="Modality"
                  onChange={e => setFilters(f => ({ ...f, modality: e.target.value }))}>
                  <MenuItem value="">All</MenuItem>
                  {['CT', 'MRI', 'XRAY', 'MAMMO', 'US', 'ANGIO', 'BMD'].map(m =>
                    <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
                value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
                value={filters.dateTo} onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <Button fullWidth variant="contained" startIcon={<RefreshIcon />} onClick={fetchOrders}>
                Refresh
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Stats Bar */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          {['READY', 'STARTED', 'ENDEXAM', 'QC'].map(s => {
            const count = orders.filter(o => o.orderStatus === s).length;
            return (
              <Chip key={s} label={`${s}: ${count}`} color={STATUS_COLORS[s]}
                variant="filled" sx={{ fontWeight: 600, fontSize: '0.85rem' }} />
            );
          })}
          <Chip label={`Total: ${orders.length}`} variant="outlined" sx={{ fontWeight: 600 }} />
        </Box>

        {/* Table */}
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'primary.main', color: 'primary.contrastText' } }}>
                <TableCell>MRN</TableCell>
                <TableCell>ACC</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Mod</TableCell>
                <TableCell>Procedure</TableCell>
                <TableCell>Referrer / Dept</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell></TableRow>
              ) : orders.length === 0 ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No exams in queue</Typography>
                </TableCell></TableRow>
              ) : orders.map(order => (
                <TableRow key={order._id} hover sx={{
                  '& td': { py: '3px !important', px: '8px !important', fontSize: '0.75rem', height: '34px' },
                  bgcolor: order.priority === 'STAT' ? 'error.dark' : order.priority === 'URGENT' ? 'warning.dark' : undefined,
                  '&:hover': { opacity: 0.9 }
                }}>
                  <TableCell>{order.patientMrn}</TableCell>
                  <TableCell><Typography variant="body2" color="success.main">{order.accessionNumber}</Typography></TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box component="span" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>
                        {order.patientName?.replace('^', ' ')}
                      </Box>
                      {order.patientSex && (
                        <Box component="span" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                          ({order.patientSex})
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell><Chip label={order.modality} size="small" /></TableCell>
                  <TableCell>
                    <Typography variant="body2">{order.procedureDescription || order.procedureCode}</Typography>
                    {order.notes && <Typography variant="caption" color="warning.main">{order.notes}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{order.referrerName}</Typography>
                    <Typography variant="caption" color="text.secondary">{order.departmentName}</Typography>
                  </TableCell>
                  <TableCell><Chip label={order.orderStatus} size="small" color={STATUS_COLORS[order.orderStatus]} /></TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {order.examStartedAt ? new Date(order.examStartedAt).toLocaleTimeString() : 
                       order.readyAt ? new Date(order.readyAt).toLocaleTimeString() : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>{renderActions(order)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
